import { apiClient } from './apiClient';
import { indexedDbService, ChangeType, PendingChange } from './indexedDbService';
import { appointmentService } from './appointmentService';
import { appointmentTypeService } from './appointmentTypeService';
import { businessService } from './businessService';
import { apiHealthService } from './apiHealthService';
import { registerNetworkListeners, isBrowserOffline } from '../utils/networkUtils';

// Constants
const CONNECTIVITY_CHECK_INTERVAL = 30000; // 30 seconds
const SYNC_METADATA_LAST_SYNC = 'lastSync';

// Event names
export const OFFLINE_EVENTS = {
  CONNECTIVITY_CHANGED: 'connectivity-changed',
  SYNC_STARTED: 'sync-started',
  SYNC_COMPLETED: 'sync-completed',
  SYNC_FAILED: 'sync-failed',
  PENDING_CHANGES_UPDATED: 'pending-changes-updated'
};

export const getOnlineStatus = (): boolean => {
    return navigator.onLine;
};

class OfflineService {
  // Default to online initially to prevent the 30-second offline period
  private isOnline: boolean = true;
  private checkIntervalId: number | null = null;
  private eventTarget: EventTarget = new EventTarget();
  private isSyncing: boolean = false;
  private initialCheckDone: boolean = false;
  private cleanupNetworkListeners: (() => void) | null = null;

  constructor() {
    console.log('Initializing OfflineService - default to online mode');

    // Clean up any existing intervals first (for hot module replacement)
    this.stopConnectivityChecks();

    this.forceOnlineMode();

    // Use the utility function to register network listeners
    this.cleanupNetworkListeners = registerNetworkListeners(
      () => this.handleOnline(),
      () => this.handleOffline()
    );

    // Initial check of browser's online status
    const browserOnline = !isBrowserOffline();
    console.log(`Initial browser online status: ${browserOnline ? 'Online' : 'Offline'}`);

    // Start connectivity check
    this.startConnectivityCheck();

    // Skip immediate connectivity check to avoid blocking initial page loads
    // The periodic check will handle this, and we assume online unless proven otherwise
    this.initialCheckDone = true;
    console.log(`Skipping immediate connectivity check to avoid blocking page loads. Online status: ${this.isOnline ? 'Online' : 'Offline'}`);
  }

  /**
   * Force the service into online mode (used for initialization)
   */
  private forceOnlineMode(): void {
    this.isOnline = true;
    console.log('Forced online mode for initial app state');
  }

  /**
   * Start periodic connectivity checks
   */
  private startConnectivityCheck(): void {
    // Clear any existing interval first
    if (this.checkIntervalId !== null) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    
    // Start a new interval
    this.checkIntervalId = window.setInterval(() => {
      this.checkConnectivity().catch(error => {
        console.error('Error during scheduled connectivity check:', error);
      });
    }, CONNECTIVITY_CHECK_INTERVAL);
    
    console.log(`Started connectivity checks (interval: ${CONNECTIVITY_CHECK_INTERVAL}ms)`);
  }

  /**
   * Stop periodic connectivity checks
   */
  private stopConnectivityChecks(): void {
    if (this.checkIntervalId !== null) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
      console.log('Stopped connectivity checks');
    }
  }

  /**
   * Clean up all resources (useful for hot module replacement)
   */
  public cleanup(): void {
    console.log('Cleaning up OfflineService...');
    this.stopConnectivityChecks();
    
    if (this.cleanupNetworkListeners) {
      this.cleanupNetworkListeners();
      this.cleanupNetworkListeners = null;
    }
  }

  /**
   * Check connectivity by making a request to the health endpoint
   */
  async checkConnectivity(): Promise<boolean> {
    try {
      // If browser reports offline, don't even try the API check
      if (isBrowserOffline()) {
        console.log('Browser reports offline - skipping API health check');
        
        // If we were previously online, update status
        if (this.isOnline) {
          this.setOfflineStatus(false);
        }
        
        return false;
      }
      
      // Perform the API health check
      const isFirstCheck = !this.initialCheckDone;
      console.log(`Checking API connectivity... ${isFirstCheck ? '(initial check)' : ''}`);
      
      const isConnected = await apiHealthService.isAvailable(true); // Force a fresh check
      
      console.log(`API health check result: ${isConnected ? 'Available' : 'Unavailable'}`);
      
      // Update offline status if it has changed or if this is the initial check
      if (this.isOnline !== isConnected || isFirstCheck) {
        this.setOfflineStatus(isConnected);
      }
      
      return isConnected;
    } catch (error) {
      console.error('Error checking connectivity:', error);
      
      // On error during initial check, default to online if browser is online
      if (!this.initialCheckDone && navigator.onLine) {
        console.log('Initial check error but browser is online - defaulting to online');
        this.setOfflineStatus(true);
        return true;
      } else {
        // Otherwise, assume we're offline
        this.setOfflineStatus(false);
        return false;
      }
    }
  }

  /**
   * Set the offline status and dispatch events
   */
  private setOfflineStatus(isConnected: boolean): void {
    const previousStatus = this.isOnline;
    this.isOnline = isConnected;
    
    // Note: initialCheckDone should only be set by the checkConnectivity method
    
    if (previousStatus !== isConnected) {
      console.log(`Connectivity changed: ${isConnected ? 'Online' : 'Offline'}`);
      this.dispatchEvent(OFFLINE_EVENTS.CONNECTIVITY_CHANGED, { isOnline: isConnected });
      
      // If we're back online, start sync process
      if (isConnected) {
        this.syncPendingChanges();
      }
    } else {
      console.log(`Connectivity status confirmed: ${isConnected ? 'Online' : 'Offline'}`);
    }
  }

  /**
   * Get the current online status
   * @returns The current online status
   */
  getOnlineStatus(): boolean {
    // If we haven't done the initial check yet, always return true (online)
    // This ensures the app starts in online mode until we know for sure
    if (!this.initialCheckDone) {
      const browserOnline = navigator.onLine;
      console.log(`Initial connectivity check not completed yet - defaulting to online (Browser reports: ${browserOnline ? 'Online' : 'Offline'})`);
      return true;
    }
    
    // If the browser reports offline, we're definitely offline
    if (isBrowserOffline()) {
      console.log('Browser reports offline - returning offline status');
      return false;
    }
    
    console.log(`Current online status: ${this.isOnline ? 'Online' : 'Offline'} Browser online status: ${navigator.onLine ? 'Online' : 'Offline'}`);
    return this.isOnline;
  }

  /**
   * Sync pending changes with the server
   */
  async syncPendingChanges(): Promise<void> {
    // If we're offline or already syncing, don't try to sync
    if (!this.isOnline || this.isSyncing) {
      return;
    }
    
    try {
      this.isSyncing = true;
      
      // Dispatch sync started event
      this.dispatchEvent(OFFLINE_EVENTS.SYNC_STARTED, {});
      
      // Get all pending changes
      const pendingChanges = await indexedDbService.getPendingChanges();
      
      if (pendingChanges.length === 0) {
        console.log('No pending changes to sync');
        this.dispatchEvent(OFFLINE_EVENTS.SYNC_COMPLETED, { changesApplied: 0 });
        this.isSyncing = false;
        return;
      }
      
      console.log(`Syncing ${pendingChanges.length} pending changes`);
      
      // Sort changes by timestamp (oldest first)
      const sortedChanges = [...pendingChanges].sort((a, b) => a.timestamp - b.timestamp);
      
      let changesApplied = 0;
      
      // Process each change
      for (const change of sortedChanges) {
        try {
          await this.processChange(change);
          await indexedDbService.removePendingChange(change.id);
          changesApplied++;
          
          // Update pending changes count
          const remainingChanges = sortedChanges.length - changesApplied;
          console.log(`Changes applied: ${changesApplied}, Remaining: ${remainingChanges}`);
          this.dispatchEvent(OFFLINE_EVENTS.PENDING_CHANGES_UPDATED, {
            pendingChangesCount: remainingChanges
          });
        } catch (error) {
          console.error(`Error processing change ${change.id}:`, error);
          // Continue with next change
        }
      }
      
      // Update last sync time
      await indexedDbService.setSyncMetadata(SYNC_METADATA_LAST_SYNC, new Date().toISOString());
      
      // Dispatch sync completed event
      // NOTE: MainCalendar's handleSyncCompleted will do the data refresh
      // Do NOT call refreshData() here - it causes duplicate merge operations
      // which race with MainCalendar's refresh and cause flickering
      this.dispatchEvent(OFFLINE_EVENTS.SYNC_COMPLETED, { changesApplied });
      
      // Double-check pending changes count after sync
      await this.refreshPendingChangesCount();
    } catch (error) {
      console.error('Error syncing pending changes:', error);
      this.dispatchEvent(OFFLINE_EVENTS.SYNC_FAILED, { error });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Process a single change
   */
  private async processChange(change: PendingChange): Promise<void> {
    console.log(`Processing change ${change.id}:`, change);
    
    try {
      // Special handling for AppointmentException entities
      if (change.entityType === 'AppointmentException') {
        console.log('Processing appointment exception change:', change);
        
        // Extract the exception data
        const exceptionData = change.data;
        
        if (exceptionData.isRecurringInstance && exceptionData.originalAppointment) {
          console.log('Processing recurring instance exception:', exceptionData);
          
          // For recurring appointments, we need to create an exception
          // Use the appointmentService directly to create the exception
          try {
            // Prepare the appointment data for the exception
            const appointmentData = exceptionData.updatedAppointmentData;
            
            // Make sure the originalDate is properly set
            appointmentData.originalDate = new Date(exceptionData.originalAppointment.startTime);
            
            // Check if we're moving to a day that already has an instance of this recurring series
            const originalDate = new Date(exceptionData.originalAppointment.startTime);
            const targetDate = new Date(appointmentData.startTime);
            
            // Only perform this check if we're moving to a different day
            if (originalDate.toDateString() !== targetDate.toDateString()) {
              console.log('Moving recurring instance to a different day:', {
                originalDate: originalDate.toDateString(),
                targetDate: targetDate.toDateString()
              });
              
              // Get the recurring appointment to check for conflicts
              try {
                const parentId = exceptionData.appointmentId;
                const parentAppointment = await appointmentService.getAppointment(parentId);
                
                if (parentAppointment && parentAppointment.recurrenceRule) {
                  // Check if the target date might naturally have an instance based on the recurrence rule
                  // For this, we'd need to implement complex recurrence rule parsing in JavaScript
                  // Instead, we'll get all appointments for the month and check
                  
                  // Get month view of appointments for the parent series
                  const monthStart = new Date(targetDate);
                  monthStart.setDate(1); // First of the month
                  
                  const monthEnd = new Date(targetDate);
                  monthEnd.setMonth(monthEnd.getMonth() + 1);
                  monthEnd.setDate(0); // Last of the month
                  
                  const appointments = await appointmentService.getAppointments({ 
                    startDate: monthStart.toISOString(),
                    endDate: monthEnd.toISOString()
                  });
                  
                  // Check if any appointments from this series fall on the target date
                  const hasAppointmentOnTargetDate = appointments.some(a => 
                    a.recurringAppointmentId === parentId &&
                    new Date(a.startTime).toDateString() === targetDate.toDateString()
                  );
                  
                  if (hasAppointmentOnTargetDate) {
                    console.error('Cannot move recurring instance: Target date already has an instance of this series');
                    
                    // Don't proceed with the change, remove it from pending changes
                    await indexedDbService.removePendingChange(change.id);
                    
                    // Notify user that this change couldn't be applied
                    this.dispatchEvent(OFFLINE_EVENTS.SYNC_FAILED, { 
                      error: new Error("Cannot move appointment to a date that already has another instance of this recurring series")
                    });
                    
                    return;
                  }
                }
              } catch (checkError) {
                console.warn('Error checking for potential recurring instance conflict:', checkError);
                // Continue with the update anyway, the server will reject if needed
              }
            }
            
            console.log('Creating appointment exception with data:', {
              appointmentId: exceptionData.appointmentId,
              originalDate: appointmentData.originalDate,
              updatedData: appointmentData
            });
            
            // Call the appropriate service method
            const response = await appointmentService.updateRecurringInstance(
              exceptionData.appointmentId, 
              appointmentData
            );
            
            // If the response indicates a conflict with an existing instance, handle it
            if (response?.errorMessage && response.errorMessage.includes('already has an instance')) {
              console.error('Server rejected the move because the target date already has an instance:', response.errorMessage);
              
              // Notify the user
              this.dispatchEvent(OFFLINE_EVENTS.SYNC_FAILED, { 
                error: new Error(response.errorMessage)
              });
            }
            
            console.log('Appointment exception created successfully:', response);
            
            // Remove the processed change
            await indexedDbService.removePendingChange(change.id);
            return;
          } catch (exceptionError) {
            console.error('Error creating appointment exception:', exceptionError);
            throw exceptionError;
          }
        }
      }
      
      // Regular change processing for other entity types
      // Normalize dates in the change data to ensure consistent timezone handling
      const normalizedData = this.normalizeDates(change.data);
      console.log(`Normalized data for change ${change.id}:`, {
        before: change.data,
        after: normalizedData
      });
      
      let response;
      
      switch (change.method.toUpperCase()) {
        case 'POST':
          response = await apiClient.post(change.endpoint, normalizedData);
          break;
        case 'PUT':
          response = await apiClient.put(change.endpoint, normalizedData);
          break;
        case 'DELETE':
          response = await apiClient.delete(change.endpoint);
          break;
        default:
          throw new Error(`Unsupported method: ${change.method}`);
      }
      
      console.log(`Change ${change.id} processed successfully:`, response.data);
      
      // If this was an appointment change, we need to handle conflict resolution
      if (change.entityType === 'appointment' && 
          (change.changeType === ChangeType.UPDATE || change.changeType === ChangeType.CREATE)) {
        await this.handleAppointmentConflictResolution(change, response.data);
      }
    } catch (error: any) {
      console.error(`Error processing change ${change.id}:`, error);
      
      // If the error is a 409 Conflict, we need to handle conflict resolution
      if (error.response?.status === 409) {
        await this.handleConflictResolution(change, error.response.data);
      } else {
        throw error;
      }
    }
  }

  /**
   * Normalize dates in an object to ensure consistent timezone handling
   * This converts Date objects to ISO strings to preserve timezone information
   */
  private normalizeDates(obj: any): any {
    if (!obj) return obj;
    
    // Create a deep copy to avoid modifying the original object
    const result = JSON.parse(JSON.stringify(obj));
    
    // Get the current timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Handle special case for originalDate which is a Date object
    if (obj.originalDate instanceof Date) {
      // Convert to ISO string without the 'Z' to indicate it's a local time
      const isoString = obj.originalDate.toISOString().substring(0, 19);
      result.originalDate = isoString;
      console.log('Normalized originalDate:', {
        before: obj.originalDate,
        after: result.originalDate,
        timeZone
      });
    }
    
    // Ensure timeZoneId is set
    if (!result.timeZoneId && obj.timeZoneId) {
      result.timeZoneId = obj.timeZoneId;
    } else if (!result.timeZoneId) {
      result.timeZoneId = timeZone;
      console.log('Setting missing timeZoneId:', timeZone);
    }
    
    // Special handling for appointment times
    if (result.startTime && result.endTime) {
      // Ensure these are in the correct format (YYYY-MM-DDTHH:MM:SS)
      // without the 'Z' to indicate they are local times
      if (typeof result.startTime === 'string' && result.startTime.endsWith('Z')) {
        result.startTime = result.startTime.substring(0, 19);
      }
      if (typeof result.endTime === 'string' && result.endTime.endsWith('Z')) {
        result.endTime = result.endTime.substring(0, 19);
      }
      
      console.log('Normalized appointment times:', {
        startTime: result.startTime,
        endTime: result.endTime,
        timeZoneId: result.timeZoneId
      });
    }
    
    // Look for date strings in the object and ensure they're properly formatted
    const processObject = (o: any) => {
      if (!o || typeof o !== 'object') return;
      
      Object.keys(o).forEach(key => {
        const value = o[key];
        
        // Check if the value is a date string (ISO format or similar)
        if (typeof value === 'string' && 
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          // Ensure it's a proper ISO string without the 'Z'
          if (value.endsWith('Z')) {
            o[key] = value.substring(0, 19);
            console.log(`Normalized date string for ${key}:`, {
              before: value,
              after: o[key]
            });
          }
        } else if (typeof value === 'object') {
          // Recursively process nested objects
          processObject(value);
        }
      });
    };
    
    processObject(result);
    
    return result;
  }

  /**
   * Handle appointment conflict resolution
   */
  private async handleAppointmentConflictResolution(
    change: PendingChange, 
    serverData: any
  ): Promise<void> {
    // If the server has a more recent version, update our local copy
    if (serverData.appointment) {
      const serverAppointment = serverData.appointment;
      const localAppointment = await indexedDbService.getAppointment(serverAppointment.id);
      
      if (localAppointment) {
        const serverTimestamp = new Date(serverAppointment.createdAt).getTime();
        const localTimestamp = new Date(localAppointment.createdAt).getTime();
        
        // If server version is newer, update local copy
        if (serverTimestamp > localTimestamp) {
          await indexedDbService.updateAppointment(serverAppointment);
        }
      }
    }
  }

  /**
   * Handle conflict resolution for any entity
   */
  private async handleConflictResolution(
    change: PendingChange, 
    serverData: any
  ): Promise<void> {
    console.log('Handling conflict resolution for change:', change);
    console.log('Server data:', serverData);
    
    // For now, we'll use a "last write wins" strategy
    // In a more sophisticated implementation, we could show a conflict resolution UI
    
    // If the server has a more recent version, we'll accept it
    if (serverData.timestamp && change.timestamp < serverData.timestamp) {
      console.log('Server has more recent data, accepting server version');
      return;
    }
    
    // Otherwise, we'll retry our change
    console.log('Local change is more recent, retrying');
    await this.processChange(change);
  }

  /**
   * Refresh data from server after sync
   */
  private async refreshData(): Promise<void> {
    try {
      // Get current view dates from calendar
      const calendarElement = document.querySelector('.fc') as HTMLElement;
      
      // Default: use a 30-day range centered on today
      const today = new Date();
      let start = new Date(today);
      start.setDate(today.getDate() - 15);
      let end = new Date(today);
      end.setDate(today.getDate() + 15);
      
      // Try to get dates from the calendar API if available
      if (calendarElement && (calendarElement as any).__fullCalendar) {
        const calendarApi = (calendarElement as any).__fullCalendar;
        if (calendarApi.view?.activeStart && calendarApi.view?.activeEnd) {
          start = calendarApi.view.activeStart;
          end = calendarApi.view.activeEnd;
        }
      }
      
      // Refresh appointments
      const appointments = await appointmentService.getAppointments({
        startDate: start.toISOString(),
        endDate: end.toISOString()
      });
      
      // Merge with IndexedDB cache (preserves appointments outside this range)
      // Get businessId from appointments or use the current one
      const businessId = appointments.length > 0 ? appointments[0].businessId : undefined;
      if (businessId) {
        await indexedDbService.mergeCachedAppointments(appointments, businessId, { start, end });
      } else {
        // No appointments returned, but we still have a business context - just don't clear cache
        console.log('No appointments returned from sync, keeping existing cache');
      }
      
      // Refresh appointment types
      const appointmentTypes = await appointmentTypeService.getAppointmentTypes();
      // Ensure appointment types have the businessId field set and preserve business object
      const appointmentTypesWithBusinessId = appointmentTypes.map(appointmentType => ({
        ...appointmentType,
        businessId: businessId || appointmentType.businessId,
        // Preserve the business object if it exists
        business: appointmentType.business
      }));
      await indexedDbService.saveAppointmentTypes(appointmentTypesWithBusinessId);
      
      // Refresh businesses
      const businesses = await businessService.getBusinesses();
      await indexedDbService.saveBusinesses(businesses);
      
      // Refresh business users for the current business
      const currentBusiness = businesses[0]; // Assuming the first business is the current one
      if (currentBusiness) {
        const users = await businessService.getBusinessUsers(currentBusiness.id);
        await indexedDbService.saveBusinessUsers(users);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }

  /**
   * Add an event listener
   */
  addEventListener(eventName: string, listener: EventListener): void {
    this.eventTarget.addEventListener(eventName, listener);
  }

  /**
   * Remove an event listener
   */
  removeEventListener(eventName: string, listener: EventListener): void {
    this.eventTarget.removeEventListener(eventName, listener);
  }

  /**
   * Dispatch an event
   */
  private dispatchEvent(eventName: string, detail: any): void {
    const event = new CustomEvent(eventName, { detail });
    this.eventTarget.dispatchEvent(event);
  }

  /**
   * Get the count of pending changes
   */
  async getPendingChangesCount(): Promise<number> {
    const pendingChanges = await indexedDbService.getPendingChanges();
    return pendingChanges.length;
  }

  /**
   * Check if there are any pending changes
   */
  async hasPendingChanges(): Promise<boolean> {
    const count = await this.getPendingChangesCount();
    return count > 0;
  }

  /**
   * Force a refresh of the pending changes count and notify listeners
   */
  async refreshPendingChangesCount(): Promise<number> {
    const count = await this.getPendingChangesCount();
    this.dispatchEvent(OFFLINE_EVENTS.PENDING_CHANGES_UPDATED, {
      pendingChangesCount: count
    });
    return count;
  }

  /**
   * Dispose of the service, cleaning up resources
   */
  dispose(): void {
    // Stop connectivity checks
    this.stopConnectivityChecks();
    
    // Clean up network listeners
    if (this.cleanupNetworkListeners) {
      this.cleanupNetworkListeners();
      this.cleanupNetworkListeners = null;
    }
    
    console.log('OfflineService disposed');
  }

  // Add private methods to handle online/offline events
  private handleOnline(): void {
    console.log('Browser went online');
    this.checkConnectivity();
  }

  private handleOffline(): void {
    console.log('Browser went offline');
    this.setOfflineStatus(false);
  }
}

export const offlineService = new OfflineService(); 